import { PagedResult, panelGet, panelPost, panelPut } from "./panel.api";

/**
 * workflow — /workflow (Panel Contract API V4)
 *
 * The approval flow for a work order once it has left the creation wizard:
 * an approver approves or rejects it, anyone on the routing can leave a
 * comment against one of its documents, and the creator can cancel it.
 *
 * Everything here is addressed by `envelopeId` — the same identifier the
 * work-order list and detail endpoints use — so these calls slot straight into
 * the drilldown without a second lookup.
 *
 * Note the asymmetry, which mirrors the backend signatures exactly:
 * `approve` carries envelopeId as a QUERY PARAM with no body, while
 * `reject` / `cancel` take a JSON body (they need a comment alongside it).
 */

/** A row from GET /workflow/getCommentsList. */
export interface WorkflowComment {
  id?: number;
  envelopeId?: string;
  documentId?: number | string;
  documentName?: string;
  documentVersion?: string | number;
  comment?: string;
  commenterName?: string;
  commenterEmail?: string;
  commenterRole?: string;
  actionType?: string;
  commentedOn?: string;
  [key: string]: any;
}

export interface WorkflowCommentListParams {
  envelopeId: string;
  page?: number;
  size?: number;
  searchText?: string;
  order?: string;
  orderBy?: string;
  /** Narrows to one kind of entry, e.g. only rejections. */
  actionType?: string;
}

export interface WorkflowCommentPayload {
  envelopeId: string;
  /** Which document the comment is against — numeric on this endpoint. */
  documentId?: number;
  comment: string;
  /** The commenter's panel role, so the trail records who spoke as what. */
  commenterRole?: string;
}

export interface WorkflowRejectPayload {
  envelopeId: string;
  documentId?: string;
  comment: string;
}

export interface WorkflowCancelPayload {
  envelopeId: string;
  documentId?: string;
  comment: string;
  /** True when a watcher, rather than the creator, pulled the work order. */
  cancelledByWatcher?: boolean;
}

/** A reviewer/approver on the routing of an existing work order. */
export interface WorkflowReviewer {
  id?: number;
  envelopeId?: string;
  reviewerName?: string;
  email?: string;
  division?: string;
  designation?: string;
  subsidiary?: string;
  authorityGroup?: string;
  budget?: string;
  routingOrder?: number;
  role?: string;
  [key: string]: any;
}

export const workflowApi = {
  /** Approve the work order at the caller's step in the routing. */
  approve: (envelopeId: string) => panelPut("/workflow/approve", undefined, { envelopeId }),

  /** Reject it. The comment is the mandatory reason shown on the Comments tab. */
  reject: (payload: WorkflowRejectPayload) => panelPut("/workflow/reject", payload),

  /** Withdraw the work order from the flow — creator or watcher initiated. */
  cancel: (payload: WorkflowCancelPayload) =>
    panelPut("/workflow/cancel", { cancelledByWatcher: false, ...payload }),

  /** Leave a comment against one of the work order's documents. */
  comment: (payload: WorkflowCommentPayload) => panelPost("/workflow/comments", payload),

  /** Resend/Reply — send it on again with a comment (e-POD's resend-document body). */
  resend: (payload: { envelopeId: string; comment: string }) => panelPut("/workflow/resend", payload),

  /** Paged comment trail for one work order, newest first. */
  commentList: ({ envelopeId, ...params }: WorkflowCommentListParams) =>
    panelGet<PagedResult<WorkflowComment>>("/workflow/getCommentsList", {
      page: 0,
      size: 20,
      order: "desc",
      orderBy: "commentedOn",
      envelopeId,
      ...params,
    }),

  /** The routing as it currently stands — reviewers and approvers in order. */
  reviewers: (envelopeId: string) =>
    panelGet<WorkflowReviewer[]>("/workflow/get/reviewers/list", { envelopeId }),

  /** Replace the routing. Takes the FULL array — rows omitted are dropped. A
      reviewer has no designation, so its role goes there ("REVIEWER"), as
      create and draft-create send it. */
  editReviewers: (rows: WorkflowReviewer[]) =>
    panelPut(
      "/workflow/edit-reviewer",
      rows.map((row) => ({ ...row, designation: row.designation || row.role }))
    ),
};
